import React, { useState } from 'react';
import { ItineraryImage } from 'containers/Itinerary/items/ItineraryImage';
import FluidButton from 'ui/FluidButton';
import { ButtonWithDropdown, iButtonWithDropdownProps } from 'ui/ButtonWithDropdown';
import classNames from 'classnames';
import { SvgIcon } from 'ui/SvgIcon';
import HamgburgerIcon from 'ui/Icons/hamburger.component.svg';
import { theme } from '../../../tailwind.config';
import { ImageUpload } from 'services/ItineraryManagerApi/types';
import { AuthorizeUserModal } from './AuthorizeUserModal';
import { useSelector } from 'react-redux';
import * as AuthSelectors from 'store/modules/auth/selectors';
import { useVoucherPdfExport } from './useVoucherPdfExport';
import { makeItineraryManagerApi } from 'services/ItineraryManagerApi';
import { TCountryCode } from 'interfaces';
import { VoucherHotel, BMSVoucherCountry } from 'services/ItineraryManagerApi/types';

interface iVoucherHeaderProps {
  upload: ImageUpload | undefined;
  changeLogoButton?: React.ReactNode;
  mode: 'edit' | 'view';
  onSave?: () => void;
  onCancel?: () => void;
  onEdit?: () => void;
  isSaveLoading?: boolean;
  isEditedWithoutSaving?: boolean;
  // Mobile navigation props
  availableTabs?: Array<{ id: string; label: string }>;
  currentTab?: string;
  onTabChange?: (tabId: string) => void;
  // Voucher UUID for AuthorizeUserModal
  voucherUuid?: string;
  // Access UUID for PDF generation
  accessUuid?: string | null;
  // Voucher data for PDF announcements
  hotels?: VoucherHotel[];
  countries?: BMSVoucherCountry[];
  travelCompanyUuid?: string;

  destinationFiles: { path: string; countryCode: TCountryCode }[];
}

export const VoucherHeader = (props: iVoucherHeaderProps) => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const isInternalUser = useSelector(AuthSelectors.isInternalUser);
  const { generatePdf } = useVoucherPdfExport(makeItineraryManagerApi(), props.voucherUuid, props.accessUuid);

  // Generate dropdown options dynamically from availableTabs
  const generateDropdownOptions = () => {
    // Filter out "Voucher Details" if it's the only tab (no destinations)
    const filteredTabs = props.availableTabs?.filter(tab => {
      // Keep all tabs if there are multiple tabs (destinations exist)
      if (props.availableTabs && props.availableTabs.length > 1) return true;
      // If only one tab and it's "Voucher Details", exclude it from dropdown
      return tab.id !== 'voucher-details';
    }) || [];

    const tabOptions = filteredTabs.map(tab => ({
      label: tab.label,
      onClick: () => props.onTabChange?.(tab.id),
      isSelected: props.currentTab === tab.id,
    }));

    const baseOptions: iButtonWithDropdownProps['dropdownOptions'] = [...tabOptions];

    if (props.mode === 'edit') {
      baseOptions.push(
        {
          label: 'Discard',
          onClick: () => props.onCancel?.(),
        },
        {
          label: 'Save',
          onClick: () => props.onSave?.(),
        }
      );
    } else {
      // so we're in view mode here
      // Add Share option for internal users
      if (isInternalUser) {
        baseOptions.push({
          label: 'Edit',
          onClick: () => props.onEdit?.(),
        });

        baseOptions.push({
          label: 'Share',
          onClick: () => setShowModal(true),
        });
      }

      baseOptions.push({
        label: 'Export PDF',
        onClick: () => generatePdf.mutate(),
      });
    }

    return baseOptions;
  };

  return (
    <div
      id="itinerary-header"
      className="sticky w-full my-0 top-0 left-0 right-0 flex flex-col bg-white z-[9999] shadow-sm"
    >
      <div className="flex flex-row items-center justify-between px-[20px] min-h-[80px] max-h-[80px]">
        <div className="flex items-center gap-2 h-full py-10px md:max-w-[50%]">
          {props.upload && (
            <ItineraryImage
              index={0}
              className="h-full"
              imgClassName="max-h-[70px] max-w-[218px]"
              upload={props.upload}
            />
          )}
          {props.changeLogoButton ? props.changeLogoButton : null}
        </div>

        {props.mode === 'edit' && (
          <div className="cta-buttons items-center gap-2 py-10px hidden md:flex">
            <FluidButton type="secondary" onClick={props.onCancel} disabled={props.isSaveLoading}>
              Discard
            </FluidButton>
            <FluidButton
              type="primary"
              onClick={props.onSave}
              isLoading={props.isSaveLoading}
              disabled={!props.isEditedWithoutSaving}
            >
              Save
            </FluidButton>
          </div>
        )}

        {props.mode === 'view' && (
          <div className="cta-buttons items-center gap-2 py-10px hidden md:flex">
            {isInternalUser && (
              <FluidButton type="secondary" onClick={() => setShowModal(true)} className="ml-4">
                Share
              </FluidButton>
            )}
            <FluidButton type="secondary" onClick={() => generatePdf.mutate()} isLoading={generatePdf.isPending}>
              Export to PDF
            </FluidButton>
            {isInternalUser && (
              <FluidButton type="primary" onClick={props.onEdit}>
                Edit
              </FluidButton>
            )}
          </div>
        )}

        <div className="block md:hidden">
          <ButtonWithDropdown
            buttonLabel={
              <SvgIcon IconComponent={HamgburgerIcon} defaultFill={theme.colors['black']} width="16px" height="16px" />
            }
            buttonClassName={classNames(
              'h-full border-y-4 border-y-white text-black o:p-0 o:w-[32px] o:h-[32px] o:bg-gray-10 o:hover:bg-gray-20 rounded-full'
            )}
            closeOnSelect={true}
            showChevron={false}
            dropdownClassName="o:normal-case text-[15px] o:font-pt-sans o:text-left"
            dropdownPanelClassName="shadow-lg p-[10px]"
            dropdownPosition="left"
            dropdownOptions={generateDropdownOptions()}
          />
        </div>
      </div>

      {showModal && props.voucherUuid && (
        <AuthorizeUserModal voucherUuid={props.voucherUuid} onClose={() => setShowModal(false)} />
      )}
    </div>
  );
};
